import type * as React from 'react';
import type { NotificationProps } from '@/shared/types/message';
import type { NotificationCardProps, NotificationIntent } from '@/modules/Notification';
import type { AnimationAlert } from '@/shared/types/alert';

/**
 * Resuelve el callback de clic en la tarjeta desde la cola del servicio o overrides de `notificationCard`.
 *
 * @param n - Entrada de cola (`onClick` plano o `events.onClick` agrupado).
 * @returns Handler de clic en superficie o `undefined`.
 */
export function resolveNotificationClickHandler(
  n: Pick<NotificationProps, 'onClick' | 'notificationCard'>
): (() => void) | undefined {
  return n.onClick ?? n.notificationCard?.events?.onClick;
}

/**
 * Mapea una notificación del servicio agrupado al componente **Notification** del design system.
 *
 * @param n - Entrada de la cola del servicio agrupado.
 * @param intent - Intención visual compartida del mazo (`brand`, `neutral`, …).
 * @param options - Overrides de presentación para esta capa (animación, timer, footer, botones).
 * @returns Props listas para `<_notification.Notification />` (`NotificationCardProps`).
 */
export function messageQueueEntryToNotification(
  n: NotificationProps,
  intent: NotificationIntent,
  options: {
    /** Clases Tailwind en el contenedor de la tarjeta (ancho, anulación de transición en capas, …). */
    className?: string;
    /** Indica fase de salida para animación de cierre. */
    isLeaving: boolean;
    /** Duración en ms para la barra de progreso; `undefined` oculta la barra en esa capa. */
    duration?: number;
    /** Atenúa la barra sin desmontarla (p. ej. hover en el mazo). */
    progressBarClassName?: string;
    /** Animación del `Alert` interno (`none` en capas inferiores del stack). */
    animation: AnimationAlert;
    /** Si es true, propaga textos y callbacks de botones primario/secundario desde `n`. */
    showActionButtons: boolean;
    /** Si se define, sustituye `n.showCloseButton` (p. ej. capas traseras del mazo). */
    showCloseButton?: boolean;
    /** Pie opcional reemplazando el slot `footer` de la tarjeta. */
    footer?: React.ReactNode | null;
    /** Cerrar desde el botón X (o overlay) con evento de botón. */
    onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    /** Click en la superficie de la tarjeta cuando aplique (si se omite, se resuelve desde `n`). */
    onClick?: () => void;
  }
): NotificationCardProps {
  const surfaceClick = 'onClick' in options ? options.onClick : resolveNotificationClickHandler(n);
  const leading = n.avatar
    ? { source: 'usuario' as const, imageUrl: n.avatar, showIntentBadge: true }
    : { source: 'general' as const };

  const showClose = options.showCloseButton ?? n.showCloseButton;

  return {
    open: true,
    content: {
      title: n.title ?? '',
      subtitle: n.message ?? n.subtitle ?? '',
      description: n.description,
    },
    leading,
    appearance: {
      intent,
      variant: n.variant ?? 'outlined',
      size: n.size ?? 'sm',
      animation: options.animation,
      showCloseButton: showClose,
      isLeaving: options.isLeaving,
      className: options.className,
    },
    meta: {
      presentation: 'popup',
      duration: options.duration,
      progressBarClassName: options.progressBarClassName,
    },
    events: {
      onClose: options.onClose,
      onPrimaryClick: options.showActionButtons ? n.onPrimaryClick : undefined,
      onSecondaryClick: options.showActionButtons ? n.onSecondaryClick : undefined,
      primaryButtonText: options.showActionButtons ? n.primaryButtonText : undefined,
      secondaryButtonText: options.showActionButtons ? n.secondaryButtonText : undefined,
      onClick: surfaceClick,
    },
    footer: options.footer ?? undefined,
  };
}

/**
 * Fusiona overrides parciales sobre una tarjeta de notificación ya construida.
 *
 * @param base - Props completas de `Notification` (con `open`).
 * @param over - Parcial por bloques (`content`, `appearance`, `meta`, `events`, `footer`) sin obligar `open`.
 * @returns Nueva tarjeta con bloques fusionados superficialmente.
 */
/**
 * Fusiona `events` sin que claves `undefined` en el override borren handlers del deck (p. ej. `onClose`).
 */
function mergeNotificationCardEvents(
  base: NotificationCardProps['events'],
  over?: Partial<NonNullable<NotificationCardProps['events']>>
): NotificationCardProps['events'] {
  if (!over) return base;
  const merged = { ...base };
  for (const [key, value] of Object.entries(over) as [
    keyof NonNullable<NotificationCardProps['events']>,
    unknown,
  ][]) {
    if (value !== undefined) {
      (merged as Record<string, unknown>)[key] = value;
    } else if (key in over) {
      delete (merged as Record<string, unknown>)[key];
    }
  }
  return merged;
}

export function mergeNotificationCard(
  base: NotificationCardProps,
  over?: Partial<Omit<NotificationCardProps, 'open'>>
): NotificationCardProps {
  if (!over) return base;
  return {
    ...base,
    content: over.content ? { ...base.content, ...over.content } : base.content,
    leading: over.leading ? { ...base.leading, ...over.leading } : base.leading,
    appearance: over.appearance ? { ...base.appearance, ...over.appearance } : base.appearance,
    meta: over.meta ? { ...base.meta, ...over.meta } : base.meta,
    events: mergeNotificationCardEvents(base.events, over.events),
    footer: over.footer !== undefined ? over.footer : base.footer,
  };
}
